import {
    IContextualMenuItem,
    IContextualMenuProps,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import RetentionPolicyTag from 'owa-service/lib/contract/RetentionPolicyTag';
import RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import { ARCHIVE_FOLDERS_TREE_TYPE } from 'owa-folders';
import { FolderForestTreeType } from 'owa-favorites-types';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import getFolderTypeFromDistinguishedFolderId from '../helpers/getFolderTypeFromDistinguishedFolderId';
import generateArchivePolicyList from './generateArchivePolicyList';
import generateDeletePolicyList from './generateDeletePolicyList';

/**
 * Utility to create the retention policy subMenuProps
 * @param retentionPolicyTagList list of all possible policy tags
 * @param showTagSelection whether to show a selected sub menu item as checked
 * @param selectedArchiveTag currently selected archive tag
 * @param selectedDeleteTag currently selected delete tag
 * @param treeType treetype of the folder to which this item belongs
 * @param distinguishedFolderId distinguished folder id of the folder
 * @param hasArchive whether an archive is present
 * @param onArchivePolicyClickedCallback archive policy clicked callback
 * @param onDeletePolicyClickedCallback delete policy clicked callback
 */
export default function getRetentionPolicySubMenuProps(config: {
    retentionPolicyTagList: RetentionPolicyTag[];
    showTagSelection: boolean;
    selectedArchiveTag: RetentionTagType;
    selectedDeleteTag: RetentionTagType;
    treeType: FolderForestTreeType;
    distinguishedFolderId: DistinguishedFolderIdName;
    hasArchive: boolean;
    onArchivePolicyClickedCallback: (
        ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: IContextualMenuItem
    ) => void;
    onDeletePolicyClickedCallback: (
        ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: IContextualMenuItem
    ) => void;
}): IContextualMenuProps {
    const {
        retentionPolicyTagList,
        showTagSelection,
        selectedArchiveTag,
        selectedDeleteTag,
        treeType,
        distinguishedFolderId,
        hasArchive,
        onArchivePolicyClickedCallback,
        onDeletePolicyClickedCallback,
    } = config;
    const items = [];
    const showArchivePolicies = hasArchive && treeType != ARCHIVE_FOLDERS_TREE_TYPE;
    const folderType = getFolderTypeFromDistinguishedFolderId(distinguishedFolderId);
    const visibleArchiveTags = retentionPolicyTagList
        .filter(tag => tag.IsVisible && tag.IsArchive)
        .sort(compareTagsByRetentionPeriod);
    const visibleDeleteTags = retentionPolicyTagList
        .filter(tag => tag.IsVisible && !tag.IsArchive)
        .sort(compareTagsByRetentionPeriod);

    showArchivePolicies &&
        items.push(
            ...generateArchivePolicyList(
                visibleArchiveTags,
                showTagSelection,
                selectedArchiveTag,
                folderType,
                onArchivePolicyClickedCallback
            )
        );
    items.push(
        ...generateDeletePolicyList(
            visibleDeleteTags,
            showTagSelection,
            selectedDeleteTag,
            folderType,
            distinguishedFolderId,
            onDeletePolicyClickedCallback
        )
    );
    return { items: items };
}

/**
 * Comparator to sort retention policies by retention period in ascending order
 * @param tagA
 * @param tagB
 */
function compareTagsByRetentionPeriod(tagA: RetentionPolicyTag, tagB: RetentionPolicyTag) {
    if (tagA.RetentionPeriod && tagB.RetentionPeriod) {
        return tagA.RetentionPeriod - tagB.RetentionPeriod;
    } else if (tagB.RetentionPeriod) {
        return 1;
    }
    return -1;
}
